<script>
	import { getPopupPosition } from "@xbs/lib-dom";
	import {
		singleClickHandler,
		dblClickOnGrid,
		drag,
		localeContext,
	} from "@xbs/lib-scheduler";
	import {
		createEventDispatcher,
		setContext,
		tick,
		getContext,
	} from "svelte";
	import { Globals } from "@xbs/svelte-wx";

	import Navigation from "./Navigation/Navigation.svelte";
	import Month from "./MonthView/Month.svelte";
	import Editor from "./Editor.svelte";
	import BaseColumnView from "./BaseLayout/BaseColumnView.svelte";
	import EventInfoDialog from "./EventInfoDialog.svelte";
	import CalendarsSidebar from "./CalendarSidebar/CalendarsSidebar.svelte";
	import YearView from "./YearView/YearView.svelte";
	import Agenda from "./AgendaView/Agenda.svelte";

	export let store;
	const locale = getContext(localeContext)?.getGroup("scheduler");

	const dispatch = createEventDispatcher();

	const {
		date,
		mode,
		viewModel,
		selected,
		editorShape,
		calendars,
		config,
		popupInfo,
		edit,
		datepicker,
		sidebar,
		colors,
	} = store.getReactive();

	const view = {
		day: BaseColumnView,
		week: BaseColumnView,
		month: Month,
		year: YearView,
		agenda: Agenda,
	};

	$: model = $viewModel;
	$: {
		setContext("shedulerConfig", config);
		setContext("shedulerColors", colors);
	}

	$: ({ readonly, dragCreate, dragMove, dragResize, eventInfoOnClick } =
		$config);

	let pos;
	let node;

	$: if ($popupInfo && node && selected) {
		getPos();
	}

	async function getPos() {
		await tick();
		pos = getPopupPosition(node, $selected.id);
	}

	function clickHandler(ev) {
		singleClickHandler(ev, dispatch);
	}

	function dblClickHandler(ev) {
		dblClickOnGrid(ev, dispatch, model, $config, locale);
	}

	let isValid;
	if (process.env.TRIALPACKAGE) {
		isValid = () => {
			if (typeof window === "undefined") return true;

			const l = location.hostname;
			const t = [
				// dhtmlx.com
				"ZGh0bWx4LmNvbQ==",
				// dhtmlxcode.com
				"ZGh0bWx4Y29kZS5jb20=",
				// webixcode.com
				"d2ViaXhjb2RlLmNvbQ==",
				// webix.io
				"d2ViaXguaW8=",
				// repl.co
				"cmVwbC5jbw==",
			];

			for (let i = 0; i < t.length; i++) {
				const x = window.atob(t[i]);
				if (x === l || l.endsWith("." + x)) return true;
			}

			return false;
		};
	}

</script>

{#if process.env.TRIALPACKAGE && !isValid()}
	<mark
		class="wx-event-calendar_mark"
		class:wx-event-calendar_mark--error={process.env.TRIALDATE < new Date()}>
		{window.atob('VHJpYWw=')}
	</mark>
{/if}
<Globals>
	<div class="wx-event-calendar-layout wx-event-calendar" bind:this={node}>
		<Navigation
			{date}
			{mode}
			{datepicker}
			toggleSidebar={$sidebar}
			title={$viewModel.title}
			on:action />

		<div class="wx-event-calendar-content">
			{#if $sidebar}
				<CalendarsSidebar
					{datepicker}
					{date}
					sidebar={$sidebar}
					calendars={$calendars}
					on:action />
			{/if}
			<div
				class="wx-event-calendar-grid"
				on:click={clickHandler}
				on:dblclick={dblClickHandler}
				use:drag={{ triggers: ['data-resizer', 'data-id', 'data-cell'], skip: readonly, dispatch, model, dragCreate, dragResize, dragMove, popupInfo: $popupInfo, calendars: $calendars, locale }}>
				<svelte:component
					this={view[$mode]}
					{model}
					{selected}
					calendars={$calendars}
					on:action />
			</div>

			{#if $edit}
				<Editor
					editMode={$edit}
					editorShape={$editorShape}
					calendars={$calendars}
					{datepicker}
					{model}
					event={$selected || {}}
					on:action />
			{/if}
		</div>
	</div>

	{#if $popupInfo && $selected && eventInfoOnClick && !readonly}
		<EventInfoDialog
			{pos}
			event={$selected}
			calendars={$calendars}
			on:action />
	{/if}
</Globals>

<style>
	.wx-event-calendar {
		--wx-event-calendar_hour-scale-width: 70px;
		--wx-event-calendar_hour-cell-height: 42px;
		--wx-event-calendar_month-cell-min-height: 200px;
		--wx-event-calendar_agenda-scale-width: 110px;
	}

	.wx-event-calendar-layout {
		position: relative;
		display: flex;
		flex-direction: column;
		height: 100%;
		overflow: hidden;
		font-family: var(--wx-font-family);
		font-size: var(--wx-font-size);
		background-color: var(--wx-background);
		color: var(--wx-color-font);
	}

	.wx-event-calendar-content,
	.wx-event-calendar-grid {
		flex: 1;
		display: flex;
		overflow: hidden;
	}

	.wx-event-calendar_mark {
		position: absolute;
		right: 34px;
		top: 12px;
		transform: rotate(30deg);
		color: #ccc;
		font-weight: 500;
		text-transform: uppercase;
		background: inherit;
		z-index: 10;
	}
	.wx-event-calendar_mark.wx-event-calendar_mark--error {
		color: red;
	}

</style>
